import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiService } from '../../services/api'
import type { User, PaginationParams } from '../../types'

interface UserState {
  users: User[]
  currentUser: User | null
  loading: boolean
  error: string | null
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

const initialState: UserState = {
  users: [],
  currentUser: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
}

export const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  async (pagination?: PaginationParams) => {
    const response = await apiService.getUsers(pagination)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data
  }
)

export const fetchUserById = createAsyncThunk(
  'user/fetchUserById',
  async (id: string | number) => {
    const response = await apiService.getUserById(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.user
  }
)

export const updateUser = createAsyncThunk(
  'user/updateUser',
  async ({ id, userData }: { id: string | number; userData: Partial<User> }) => {
    const response = await apiService.updateUser(id, userData)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.user
  }
)

export const deleteUser = createAsyncThunk(
  'user/deleteUser',
  async (id: string | number) => {
    const response = await apiService.deleteUser(id)
    if (response.error) {
      throw new Error(response.error)
    }
    return id
  }
)

export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async ({ name, company, phone, avatar }: { name: string; company?: string; phone?: string; avatar?: string }) => {
    const response = await apiService.updateProfile(name, company, phone, avatar)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data?.user
  }
)

export const changePassword = createAsyncThunk(
  'user/changePassword',
  async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    const response = await apiService.changePassword(currentPassword, newPassword)
    if (response.error) {
      throw new Error(response.error)
    }
    return response.data
  }
)

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentUser: (state, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload
    },
    setPagination: (state, action: PayloadAction<Partial<UserState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false
        state.users = action.payload?.data || []
        state.pagination = {
          page: action.payload?.meta?.page || 1,
          limit: action.payload?.meta?.limit || 10,
          total: action.payload?.meta?.total || 0,
          totalPages: action.payload?.meta?.totalPages || 0,
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch users'
      })
      // Fetch user by id
      .addCase(fetchUserById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.loading = false
        state.currentUser = action.payload || null
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch user'
      })
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          const userPayload = action.payload
          const index = state.users.findIndex((u) => u.id === userPayload.id)
          if (index !== -1) {
            state.users[index] = userPayload
          }
          if (state.currentUser?.id === userPayload.id) {
            state.currentUser = userPayload
          }
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to update user'
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false
        state.users = state.users.filter((u) => u.id !== action.payload)
        if (state.currentUser?.id === action.payload) {
          state.currentUser = null
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to delete user'
      })
      // Update profile
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (action.payload) {
          const userPayload = action.payload
          const index = state.users.findIndex((u) => u.id === userPayload.id)
          if (index !== -1) {
            state.users[index] = userPayload
          }
          if (state.currentUser?.id === userPayload.id) {
            state.currentUser = userPayload
          }
        }
      })
  },
})

export const { setCurrentUser, setPagination, clearError } = userSlice.actions
export default userSlice.reducer
